import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Typography,
	Row,
	Col,
	Space,
	notification,
	Divider,
	InputNumber,
	Spin,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PiHardDrivesDuotone, PiArrowLeftDuotone, PiEyeDuotone } from "react-icons/pi";
import { requestGetServerById, requestUpdateServer } from "../../services/admin.service";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

function EditServerPage() {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [fetchLoading, setFetchLoading] = useState(true);
	const [server, setServer] = useState(null);

	useEffect(() => {
		fetchServerDetails();
	}, [id]);

	const fetchServerDetails = () => {
		setFetchLoading(true);
		requestGetServerById(id)
			.then((response) => {
				const serverData = response.data.data;
				setServer(serverData);

				// Set form values
				const formValues = {
					...serverData,
					// Extract credentials
					username: serverData.credentials?.username || "",
					password: serverData.credentials?.password || "",
					key: serverData.credentials?.key || "",
					// Convert metadata object to JSON string for editing
					metadata: serverData.metadata ? JSON.stringify(serverData.metadata, null, 2) : "",
				};

				form.setFieldsValue(formValues);
			})
			.catch((error) => {
				console.error("Error fetching server:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch server details",
				});
				navigate("/settings/servers");
			})
			.finally(() => {
				setFetchLoading(false);
			});
	};

	const handleSubmit = (values) => {
		setLoading(true);

		// Parse metadata if provided
		let parsedMetadata = null;
		if (values.metadata) {
			try {
				parsedMetadata = JSON.parse(values.metadata);
			} catch (error) {
				notification.error({
					message: "Invalid JSON",
					description: "Please check your metadata JSON format",
				});
				setLoading(false);
				return;
			}
		}

		const serverData = {
			...values,
			// Ensure numeric fields are properly typed
			port: values.port || 22,
			// Handle credentials as an object
			credentials: {
				username: values.username,
				password: values.password,
				...(values.key && { key: values.key }),
			},
			// Add parsed metadata if provided
			...(parsedMetadata && { metadata: parsedMetadata }),
		};

		// Remove individual credential fields from top level
		delete serverData.username;
		delete serverData.password;
		delete serverData.key;

		requestUpdateServer(id, serverData)
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Server updated successfully",
				});
				navigate("/settings/servers");
			})
			.catch((error) => {
				console.error("Error updating server:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to update server. Please try again.",
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	if (fetchLoading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", marginTop: 50 }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button
						icon={<PiArrowLeftDuotone />}
						onClick={() => navigate("/settings/servers")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						{server ? `Edit Server: ${server.name}` : "Loading..."}
					</Title>
					<Button
						type="link"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/servers/${server._id}`)}>
						View Server
					</Button>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title={
						<Space>
							<PiHardDrivesDuotone size={20} />
							<span>Server Information</span>
						</Space>
					}>
					<Form form={form} layout="vertical" onFinish={handleSubmit}>
						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="Server Name"
									name="name"
									rules={[
										{
											required: true,
											message: "Please enter server name",
										},
									]}>
									<Input placeholder="Enter server name" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Server Type"
									name="type"
									rules={[
										{
											required: true,
											message: "Please select server type",
										},
									]}>
									<Select placeholder="Select server type">
										<Option value="WEHO">Web Hosting</Option>
										<Option value="APLA">App Launcher</Option>
										{/* <Option value="GAME">Game Server</Option>
										<Option value="VPS">VPS</Option>
										<Option value="DEDI">Dedicated Server</Option> */}
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="Hostname"
									name="hostname"
									rules={[
										{
											required: true,
											message: "Please enter hostname",
										},
									]}>
									<Input placeholder="Enter hostname" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Region"
									name="region"
									rules={[
										{
											required: true,
											message: "Please select region",
										},
									]}>
									<Select placeholder="Select region">
										<Option value="US">United States</Option>
										<Option value="EU">Europe</Option>
										<Option value="SG">Singapore (Asia)</Option>
										<Option value="AU">Australia</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="IP Address"
									name="ip"
									rules={[
										{
											required: true,
											message: "Please enter IP address",
										},
										{
											pattern:
												/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
											message: "Please enter valid IP address",
										},
									]}>
									<Input placeholder="Enter IP address" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Port"
									name="port"
									rules={[
										{
											required: true,
											message: "Please enter port number",
										},
									]}>
									<InputNumber
										placeholder="Enter port number"
										min={1}
										max={65535}
										style={{ width: "100%" }}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="Control Panel"
									name="panel"
									rules={[
										{
											required: true,
											message: "Please select control panel",
										},
									]}>
									<Select placeholder="Select control panel">
										<Option value="WEBUZO">Webuzo</Option>
										<Option value="CWP">Control Web Panel</Option>
										<Option value="COOLIFY">Coolify</Option>
										<Option value="CLOUDPANEL">Cloud Panel</Option>
										<Option value="CPANEL">cPanel</Option>
										<Option value="PLESK">Plesk</Option>
										<Option value="DIRECTADMIN">DirectAdmin</Option>
									</Select>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Status"
									name="status"
									rules={[
										{
											required: true,
											message: "Please select status",
										},
									]}>
									<Select placeholder="Select status">
										<Option value="ACTIVE">Active</Option>
										<Option value="MAINTENANCE">Maintenance</Option>
										<Option value="INACTIVE">Inactive</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Divider orientationMargin={0} orientation="left" plain>
							<strong>Server Credentials</strong>
						</Divider>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item label="Username" name="username">
									<Input placeholder="Enter username" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item label="Password" name="password">
									<Input.Password placeholder="Enter password" />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label="Key (Optional)"
									name="key"
									extra="Private key for authentication (optional)">
									<TextArea rows={4} placeholder="Enter private key" />
								</Form.Item>
							</Col>
						</Row>

						<Divider orientationMargin={0} orientation="left" plain>
							<strong>Additional Information</strong>
						</Divider>

						<Row gutter={16}>
							<Col span={24}>
								<Form.Item label="Description" name="description">
									<TextArea rows={3} placeholder="Enter server description (optional)" />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label="Metadata (JSON)"
									name="metadata"
									extra="Additional server metadata in JSON format (optional)">
									<TextArea
										rows={6}
										placeholder='Enter metadata in JSON format, e.g. {"api_endpoint": "https://api.example.com", "version": "1.0"}'
										style={{ fontFamily: 'Monaco, "Courier New", monospace' }}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Form.Item style={{ marginTop: 32 }}>
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									loading={loading}
									icon={<PiHardDrivesDuotone />}>
									Update Server
								</Button>
								<Button onClick={() => navigate("/settings/servers")}>Cancel</Button>
							</Space>
						</Form.Item>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default EditServerPage;
